import { Component, effect, inject, input, OnInit, output } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { DeleteButtonComponent } from "@components/delete-button/delete-button.component";
import { DuplicateButtonComponent } from "@components/duplicate-button/duplicate-button.component";
import { ExtendButtonComponent } from "@components/extend-button/extend-button.component";
import { ProductImageComponent } from "@components/product-image/product-image.component";
import { StandardImageComponent } from "@components/standard-image/standard-image.component";
import { ToggleItemTextVisibilityButtonComponent } from "@components/toggle-item-text-visibility-button/toggle-item-text-visibility-button.component";
import { ToggleLockButtonComponent } from "@components/toggle-lock-button/toggle-lock-button.component";
import { FloorplanFreeText } from "@domain/project/floorplan/floorplan-free-text";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanPlasticSign } from "@domain/project/floorplan/floorplan-plastic-sign";
import { FloorplanProductItem } from "@domain/project/floorplan/floorplan-product-item";
import { FloorplanSidesheetExtent } from "@domain/project/floorplan/floorplan-sidesheet-extent";
import { FloorplanSignalElement } from "@domain/project/floorplan/floorplan-signal-element";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { FloorplanService } from "@project/floorplanner/floorplan.service";
import { filter, map, Observable } from "rxjs";
import { LocalizeDatePipe } from "src/locale/localize-date";
import { LocalizeTimePipe } from "src/locale/localize-time";

@Component({
  selector: "app-floorplan-item-details",
  templateUrl: "./floorplan-item-details.component.html",
  styleUrl: "./floorplan-item-details.component.scss",
  imports: [
    UiKitModule,
    ReactiveFormsModule,
    StandardImageComponent,
    ProductImageComponent,
    ToggleLockButtonComponent,
    DeleteButtonComponent,
    DuplicateButtonComponent,
    LocalizeDatePipe,
    LocalizeTimePipe,
    ExtendButtonComponent,
    ToggleItemTextVisibilityButtonComponent,
  ],
  standalone: true,
})
export class FloorplanItemDetailsComponent implements OnInit {
  private userService = inject(UserService);
  private formBuilder = inject(FormBuilder);
  private floorplanService = inject(FloorplanService);
  private projectService = inject(ProjectService);

  readonly floorplanItem = input.required<FloorplanItem>();
  readonly showDiscontinuedWarning = input<boolean>(false);
  readonly showIncompleteWarning = input<boolean>(false);
  readonly duplicateEnabled = input<boolean>(true);
  readonly imageUrl = input<string>();
  readonly fallbackImageUrl = input<string>();
  readonly productId = input<string>();
  readonly extent = input<FloorplanSidesheetExtent>(FloorplanSidesheetExtent.None);
  readonly isExtendable = input<boolean>(false);

  readonly toggleExtendedButtonClicked = output<void>();

  protected displayNameForm?: FormGroup;
  protected notesForm?: FormGroup;
  protected isReadOnlyUser$: Observable<boolean>;
  protected hasWritePermission$: Observable<boolean | undefined>;

  protected readonly SidesheetExtent = FloorplanSidesheetExtent;

  constructor() {
    this.isReadOnlyUser$ = this.userService.user$.pipe(map((user) => user.readOnlyAccess));
    this.hasWritePermission$ = this.projectService.hasUserWritePermission$.pipe(
      filter((val): val is boolean => val !== undefined),
    );
    effect(() => {
      const floorplanItem = this.floorplanItem();
      this.displayNameForm?.reset({ name: floorplanItem.displayName }, { emitEvent: false });
      this.notesForm?.reset({ notes: floorplanItem.notes }, { emitEvent: false });
    });
  }

  ngOnInit(): void {
    this.initNotesForm();
    this.initDisplayNameForm();
  }

  protected getProductId(item: FloorplanItem): string | undefined {
    if (item instanceof FloorplanProductItem) {
      return item.config.productId;
    }
    return undefined;
  }

  protected delete() {
    this.floorplanService.deleteSelectedItem();
  }

  protected duplicate() {
    this.floorplanService.duplicateSelectedItem();
  }

  protected isSignalElement(item: FloorplanItem) {
    return item instanceof FloorplanSignalElement;
  }

  protected isPlasticSign(item: FloorplanItem) {
    return item instanceof FloorplanPlasticSign;
  }

  protected isFreeText(item: FloorplanItem) {
    return item instanceof FloorplanFreeText;
  }

  private initDisplayNameForm() {
    this.displayNameForm = this.formBuilder.group({
      name: [this.floorplanItem().displayName],
    });

    this.displayNameForm.valueChanges.subscribe(() => {
      this.update();
    });
  }

  private initNotesForm() {
    if (!this.isFreeText(this.floorplanItem())) {
      this.notesForm = this.formBuilder.group({
        notes: [this.floorplanItem().notes, Validators.maxLength(2000)],
      });

      this.notesForm.valueChanges.subscribe(() => {
        if (!this.notesForm!.valid || this.notesForm!.pristine) return;
        this.update();
      });
    }
  }

  private update() {
    this.floorplanItem().displayName = this.displayNameForm!.value.name;
    this.floorplanItem().notes = this.notesForm!.value.notes;
  }
}
